<template>
  <ArchiveModal
    :open="open"
    title="Upload settings"
    subtitle="What people can upload to the Archives. Changes apply to new uploads right away."
    size="lg"
    @close="$emit('close')"
  >
    <div v-if="loading" class="ar-loading"><span class="spinner-border spinner-border-sm mr-1"></span> Loading…</div>
    <div v-else-if="loadError" class="ar-note"><i class="bi bi-exclamation-triangle"></i><span>{{ loadError }}</span></div>
    <form v-else class="ar-settings" @submit.prevent="save">
      <div v-if="!supported" class="ar-note warn">
        <i class="bi bi-tools" aria-hidden="true"></i>
        <span>
          These settings need the latest database update before they can be changed. Ask the administrator to run the
          migration. Until then, the values below apply.
        </span>
      </div>

      <section class="ar-settings-section">
        <h6 class="ar-subhead">Largest file</h6>
        <div class="ar-size-row">
          <div class="input-group ar-size-input">
            <input
              id="ar-max-mb"
              v-model.number="form.max_file_mb"
              type="number"
              min="1"
              :max="hardMax"
              step="1"
              class="form-control"
              aria-label="Largest file, in MB"
              :disabled="!supported"
            />
            <div class="input-group-append"><span class="input-group-text">MB</span></div>
          </div>
          <div class="ar-chips" role="group" aria-label="Common sizes">
            <button
              v-for="mb in presets"
              :key="mb"
              type="button"
              class="ar-chip"
              :class="{ on: form.max_file_mb === mb }"
              :disabled="!supported"
              @click="form.max_file_mb = mb"
            >
              {{ mb >= 1024 ? mb / 1024 + " GB" : mb + " MB" }}
            </button>
          </div>
        </div>
        <p class="ar-muted mb-0">
          Up to {{ hardMax }} MB. Files are sent in pieces, so this isn't held back by the server's own upload size.
          <template v-if="storage.location === 'This server' && storage.free !== null">
            The server has {{ formatSize(storage.free) }} free.
          </template>
        </p>
        <p v-if="sizeError" class="ar-field-error">{{ sizeError }}</p>
      </section>

      <section class="ar-settings-section">
        <div class="ar-settings-head">
          <h6 class="ar-subhead mb-0">Allowed file types <span class="ar-count">{{ form.allowed.length }} of {{ allExtensions.length }}</span></h6>
          <button type="button" class="ar-link-btn" :disabled="!supported" @click="form.allowed = allExtensions.slice()">Allow all</button>
        </div>
        <ul class="ar-type-groups">
          <li v-for="group in groups" :key="group.key">
            <label class="ar-type-group">
              <input
                type="checkbox"
                :checked="groupState(group) === 'all'"
                :indeterminate.prop="groupState(group) === 'some'"
                :disabled="!supported"
                @change="toggleGroup(group, $event.target.checked)"
              />
              <span>{{ group.label }}</span>
            </label>
            <div class="ar-chips" role="group" :aria-label="group.label">
              <button
                v-for="ext in group.extensions"
                :key="ext"
                type="button"
                class="ar-chip ext"
                :class="{ on: form.allowed.includes(ext) }"
                :aria-pressed="form.allowed.includes(ext) ? 'true' : 'false'"
                :disabled="!supported"
                @click="toggleExt(ext)"
              >
                .{{ ext }}
              </button>
            </div>
          </li>
        </ul>
        <p class="ar-muted mb-0">
          Only these safe types can be allowed. Programs and scripts (such as .exe or .bat files) can never be uploaded.
        </p>
        <p v-if="!form.allowed.length" class="ar-field-error">Allow at least one file type.</p>
      </section>

      <section class="ar-settings-section">
        <h6 class="ar-subhead">Storage</h6>
        <dl class="ar-settings-facts">
          <div>
            <dt>Files are kept on</dt>
            <dd>
              <i class="bi" :class="storage.location === 'Google Drive' ? 'bi-google' : 'bi-hdd-rack'" aria-hidden="true"></i>
              {{ storage.location }}
            </dd>
          </div>
          <div v-if="storage.drive">
            <dt>Google account</dt>
            <dd>
              {{ storage.drive.email }}
              <template v-if="storage.drive.limit">
                · {{ formatSize(storage.drive.used) }} of {{ formatSize(storage.drive.limit) }} used
              </template>
            </dd>
          </div>
          <div v-if="storage.location === 'Google Drive'">
            <dt>Folder</dt>
            <dd>
              {{ storage.folder ? `“${storage.folder}”` : "The folder set in ARCHIVE_GOOGLE_FOLDER_ID" }}, with a folder for
              each cabinet. Files aren't shared by link: people open them through CCMIS, and opening one in Google
              shares it with that person's own Google account.
            </dd>
          </div>
          <div>
            <dt>Storage limits</dt>
            <dd>Set per cabinet: edit a cabinet to give it one.</dd>
          </div>
        </dl>
      </section>
    </form>

    <template #footer>
      <span v-if="updatedAt" class="ar-muted mr-auto">
        Last changed {{ formatDateTime(updatedAt) }}<template v-if="updatedBy"> by {{ updatedBy }}</template>
      </span>
      <button type="button" class="btn btn-light" @click="$emit('close')">{{ supported ? "Cancel" : "Close" }}</button>
      <button v-if="supported" type="button" class="btn btn-primary" :disabled="busy || !valid || loading" @click="save">
        <span v-if="busy" class="spinner-border spinner-border-sm mr-1"></span>Save
      </button>
    </template>
  </ArchiveModal>
</template>

<script>
import axios from "axios";
import ArchiveModal from "./ArchiveModal.vue";
import { errorMessage, formatDateTime, formatSize } from "../archiveHelpers";

const PRESETS = [25, 50, 100, 200, 500, 1024, 2048];

/** The largest upload and the allowed file types (Archives administrators). */
export default {
  name: "ArchiveSettingsModal",
  components: { ArchiveModal },
  props: {
    open: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      loadError: "",
      busy: false,
      supported: true,
      hardMax: 2048,
      groups: [],
      storage: { location: "", free: null },
      updatedAt: null,
      updatedBy: null,
      form: { max_file_mb: 200, allowed: [] },
    };
  },
  computed: {
    presets() {
      return PRESETS.filter((mb) => mb <= this.hardMax);
    },
    allExtensions() {
      return this.groups.reduce((all, g) => all.concat(g.extensions), []);
    },
    sizeError() {
      const mb = this.form.max_file_mb;
      if (!Number.isInteger(mb) || mb < 1) return "Enter a whole number of MB, at least 1.";
      if (mb > this.hardMax) return `The largest allowed is ${this.hardMax} MB.`;
      return "";
    },
    valid() {
      return !this.sizeError && this.form.allowed.length > 0;
    },
  },
  watch: {
    open(value) {
      if (value) this.load();
    },
  },
  methods: {
    formatDateTime,
    formatSize,
    apply(data) {
      this.supported = data.supported !== false;
      this.hardMax = data.hard_max_mb || 2048;
      this.groups = data.groups || [];
      this.storage = data.storage || { location: "", free: null };
      this.updatedAt = data.updated_at || null;
      this.updatedBy = data.updated_by_name || null;
      this.form = { max_file_mb: data.max_file_mb, allowed: (data.allowed_extensions || []).slice() };
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const { data } = await axios.get("api/archives/settings");
        this.apply(data);
      } catch (err) {
        this.loadError = await errorMessage(err, "The upload settings couldn't be loaded.");
      } finally {
        this.loading = false;
      }
    },
    groupState(group) {
      const on = group.extensions.filter((ext) => this.form.allowed.includes(ext)).length;
      return on === 0 ? "none" : on === group.extensions.length ? "all" : "some";
    },
    toggleGroup(group, on) {
      const rest = this.form.allowed.filter((ext) => !group.extensions.includes(ext));
      this.form.allowed = on ? rest.concat(group.extensions) : rest;
    },
    toggleExt(ext) {
      this.form.allowed = this.form.allowed.includes(ext)
        ? this.form.allowed.filter((e) => e !== ext)
        : this.form.allowed.concat(ext);
    },
    async save() {
      if (this.busy || !this.valid) return;
      this.busy = true;
      try {
        const { data } = await axios.put("api/archives/settings", {
          max_file_mb: this.form.max_file_mb,
          allowed_extensions: this.allExtensions.filter((ext) => this.form.allowed.includes(ext)),
        });
        this.apply(data);
        toastr.success(data.message, "Upload settings");
        this.$emit("saved");
        this.$emit("close");
      } catch (err) {
        toastr.error(await errorMessage(err, "The settings weren't saved."), "Upload settings");
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
